import {
  ALERT_SIGNAL_LABEL,
  ALERT_SIGNAL_UNIT,
  type AlertEvent,
  type AlertSeverity,
  type AlertSignal,
} from '@apm/contracts';
import type { Route } from '../router';
import { SIGNAL_LABEL } from '../terms';
import type { ChipTone } from '../ui';

export { ALERT_SEVERITY_LABEL as SEVERITY_LABEL } from '../terms';

/**
 * 알림 화면 표기 (10회차 결정 5).
 *
 * 색·문구·이동 경로 판정은 컴포넌트 밖 순수 함수로 둔다. 다른 화면과 같은 태도다.
 * 지속 시간 표기는 19회차에 `time.ts`로 옮겼다(화면 전체가 같은 표기를 쓴다).
 */

/** 심각도 칩. 상태 칩(status.ts)과 같은 색 계열을 쓴다 */
export const SEVERITY_TONE: Record<AlertSeverity, ChipTone> = {
  critical: 'error',
  warning: 'fail',
};

/** 경보 중 카드 테두리 */
export const SEVERITY_CARD: Record<AlertSeverity, string> = {
  critical: 'border-error/40 bg-error/5',
  warning: 'border-fail/40 bg-fail/5',
};

/** 규칙 이름 옆에 붙는 신호 이름. 엔진이 낸 알림에는 신호가 없다 */
export function signalLabel(event: AlertEvent): string {
  return event.signal === undefined ? '감시 엔진' : SIGNAL_LABEL[event.signal];
}

const SIGNALS = Object.keys(SIGNAL_LABEL) as AlertSignal[];

/**
 * 알림 문장 (44회차). 서버는 규칙 파일 쪽 신호 이름(`ALERT_SIGNAL_LABEL`, 예: "이벤트루프 p99")으로
 * 문장을 짓는다. 채팅·로그로 나가는 문장은 그대로 두고, 화면과 브라우저 알림에서만 그 이름을 화면 말
 * (`SIGNAL_LABEL`)로 바꾼다. CPU 기록 문장처럼 다른 신호의 이름이 섞여 있어도 함께 바꾼다
 */
export function alertMessage(event: AlertEvent): string {
  let text = event.message;
  for (const signal of SIGNALS) {
    const from = ALERT_SIGNAL_LABEL[signal];
    const to = SIGNAL_LABEL[signal];
    if (from !== to) text = text.replaceAll(from, to);
  }
  return text;
}

/** 값 표기. 신호마다 단위가 다르다 */
export function formatAlertValue(event: AlertEvent): string {
  if (event.signal === undefined) return `${Math.round(event.value)}건`;
  switch (ALERT_SIGNAL_UNIT[event.signal]) {
    case 'ms':
      return `${Math.round(event.value)}ms`;
    case 'pct':
      return `${event.value.toFixed(1)}%`;
    default:
      return `${Math.round(event.value)}건`;
  }
}

/**
 * 억제 표시 (24회차, 감사 D-21).
 *
 * 쿨다운·분당 상한에 걸린 알림은 화면·이력에는 남지만 채팅·로그로는 나가지 않았다.
 * "왜 이 경보는 슬랙에 없나"를 표에서 바로 알 수 있게 한 마디로 적는다.
 */
export function suppressedNote(event: AlertEvent): string | undefined {
  return event.notified ? undefined : '억제됨';
}

/** 지속 시간. 경보 중이면 지금까지, 끝났으면 걸린 시간 */
export function alertSpanMs(event: AlertEvent, now: number): number {
  return Math.max(0, (event.endedAt ?? now) - event.startedAt);
}

/** 알림 하나에서 갈 수 있는 곳. 있는 것만 준다 (결정 5) */
export interface AlertLink {
  label: string;
  route: Route;
}

export function alertLinks(event: AlertEvent): AlertLink[] {
  const out: AlertLink[] = [];
  if (event.links.traceId !== undefined) {
    out.push({
      label: '요청 상세',
      route: {
        name: 'detail',
        traceId: event.links.traceId,
        from: 'alerts',
        ...(event.service === undefined ? {} : { service: event.service }),
      },
    });
  }
  if (event.instance !== undefined) {
    out.push({
      label: '이 인스턴스 요청',
      route: {
        name: 'requests',
        query: new URLSearchParams({ instance: event.instance }).toString(),
      },
    });
  }
  if (event.links.profileId !== undefined) {
    out.push({ label: 'CPU 기록', route: { name: 'profile', id: event.links.profileId } });
  }
  if (event.links.search !== undefined && event.links.search !== '') {
    out.push({ label: '그 시간대 검색', route: { name: 'requests', query: event.links.search } });
  }
  return out;
}

/** 머리 배지. 경보 중인 것 중 가장 급한 것을 따른다 */
export function badgeSeverity(alerts: readonly AlertEvent[]): AlertSeverity | undefined {
  if (alerts.length === 0) return undefined;
  return alerts.some((alert) => alert.severity === 'critical') ? 'critical' : 'warning';
}
